import {
    Banner,
    BodyText,
    Button,
    Divider,
    Form,
    Headline,
    Icon,
    Link,
    Spinner,
    Stack,
} from '@servicetitan/design-system';
import { useDependencies } from '@servicetitan/react-ioc';
import { observer } from 'mobx-react';
import { lazy, MouseEvent, Suspense, useEffect, useState } from 'react';
import { AuthLayout } from '../../auth/components/layout/auth.layout';
import { PortalAuthStore } from '../../auth/stores/portal-auth.store';
import { LegalAgreementStore } from '../stores/legal-agreement.store';
import * as Styles from './legal-agreement-view.module.less';
import { TermsOfServiceLink } from './terms-of-service-link';

const MarkdownContent = lazy(() =>
    import('./markdown-content').then(m => ({ default: m.MarkdownContent }))
);

const SignOutLink = ({ onClick }: { onClick: () => void }) => (
    <Stack justifyContent="center">
        <Link
            href="#"
            subdued
            onClick={(e: MouseEvent) => {
                e.preventDefault();
                onClick();
            }}
        >
            Sign out
        </Link>
    </Stack>
);

const LegalAgreementContent = observer(() => {
    const [{ accept, acceptError, accepting, agreement, fetch, loadError }, { logout }] =
        useDependencies(LegalAgreementStore, PortalAuthStore);
    const [agreed, setAgreed] = useState(false);

    if (loadError || !agreement) {
        return (
            <Stack direction="column" spacing={2}>
                <Banner status="warning" icon title="We couldn't load the terms. Try again." />
                <Button width="full" primary onClick={fetch}>
                    Try again
                </Button>
                <SignOutLink onClick={logout} />
            </Stack>
        );
    }

    return (
        <Stack direction="column" spacing={2}>
            <div className={Styles.headerIcon}>
                <Icon name="check_circle" className="c-blue-500" size={24} />
            </div>

            <Headline size="medium">Review our Updated Terms &amp; Conditions</Headline>
            <BodyText subdued>
                Before you continue to your portal, we need your explicit consent to our Terms of
                Service. This is a one-time step and helps protect both you and the business.
            </BodyText>

            <Divider />

            <div className={Styles.termsBox}>
                <Suspense fallback={<Spinner />}>
                    <MarkdownContent body={agreement.body} />
                </Suspense>
            </div>

            <TermsOfServiceLink primary>
                <Icon name="description" size={16} className="m-r-half" />
                Read full Terms of Service (PDF)
            </TermsOfServiceLink>

            {acceptError && (
                <Banner status="warning" icon title="Something went wrong. Please try again." />
            )}

            <Form>
                <Form.Checkbox
                    checked={agreed}
                    onChange={(_value, checked) => setAgreed(checked)}
                    disabled={accepting}
                    label={<BodyText>I have read and agree to the Terms of Service.</BodyText>}
                />
                <Button
                    width="full"
                    primary
                    loading={accepting}
                    disabled={!agreed || accepting}
                    onClick={accept}
                >
                    Accept &amp; Continue
                </Button>
            </Form>

            <SignOutLink onClick={logout} />
        </Stack>
    );
});

export const LegalAgreementView = observer(() => {
    const [{ fetch, loading }] = useDependencies(LegalAgreementStore);

    useEffect(() => {
        fetch();
    }, [fetch]);

    return (
        <AuthLayout loading={loading} showSignInBanner={false}>
            <LegalAgreementContent />
        </AuthLayout>
    );
});
